import { useCallback, useEffect, useRef, useState } from "react";
import { SESSION_TIMEOUT_CONFIG } from "@/constants/sessionTimeout";
import { useAuth } from "@/context/auth/AuthContext";
import useServices from "@/context/services/useServices";
import { redirectToLogin } from "@/services/authRedirect";
import { sessionTimeoutInstance } from "@/hooks/useSessionTimeoutInstance";
import { sessionManager } from "@/services/sessionManager";
export const useSessionTimeout = ()=>{
    const { logout, isAuthenticated } = useAuth();
    const { authService } = useServices();
    const [showWarning, setShowWarning] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(0);
    const countdownIntervalRef = useRef(null);
    const activityThrottleRef = useRef(0);
    // Handle session timeout - logout and redirect
    const handleSessionTimeout = useCallback(async ()=>{
        setShowWarning(false);
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        // Deactivate session timeout instance
        sessionTimeoutInstance.deactivate();
        try {
            await logout();
            redirectToLogin({
                message: "Your session has expired. Please log in again."
            });
        } catch  {
            redirectToLogin();
        }
    }, [
        logout
    ]);
    // Show warning modal
    const showWarningModal = useCallback(()=>{
        // Prevent duplicate warnings
        if (showWarning) {
            return;
        }
        setShowWarning(true);
        // Start countdown
        const warningDuration = SESSION_TIMEOUT_CONFIG.WARNING_DURATION;
        let remaining = Math.floor(warningDuration / 1000);
        setTimeRemaining(remaining);
        countdownIntervalRef.current = setInterval(()=>{
            remaining -= 1;
            setTimeRemaining(remaining);
            if (remaining <= 0 && countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
                countdownIntervalRef.current = null;
            }
        }, 1000);
    }, [
        showWarning
    ]);
    // Continue session (user clicked to stay logged in)
    const continueSession = useCallback(async ()=>{
        setShowWarning(false);
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        try {
            // Call extend session endpoint to refresh tokens
            await authService.extendSession();
            sessionManager.markSessionExtended();
        } catch (error) {
            // Failed to extend session - reset local timers to give user more time
            sessionManager.resetTimers();
        }
    }, [
        authService
    ]);
    // Handle user activity (throttled session extension)
    const handleUserActivity = useCallback(async ()=>{
        // Don't extend if warning is already showing
        if (showWarning) {
            return;
        }
        // Check throttle
        const now = Date.now();
        if (now - activityThrottleRef.current < SESSION_TIMEOUT_CONFIG.SESSION_EXTENSION_THROTTLE) {
            return;
        }
        // Check if session manager allows extension
        if (!sessionManager.canExtendSession()) {
            return;
        }
        activityThrottleRef.current = now;
        // Auto-extend session after 30 minutes of the session has passed
        // This gives us a fresh 60-minute token when user is active
        const AUTO_EXTEND_AFTER = 30 * 60 * 1000; // 30 minutes
        const timeSinceLastExtension = now - sessionManager.lastExtensionTime;
        if (timeSinceLastExtension > AUTO_EXTEND_AFTER) {
            try {
                // Auto-extending session due to user activity (30+ minutes elapsed)
                // Call extend session endpoint to refresh tokens
                await authService.extendSession();
                sessionManager.markSessionExtended();
            } catch (error) {
            // Failed to auto-extend session
            // The warning modal will still appear if session truly expires
            }
        } else {
            // Just reset the inactivity timers without extending tokens
            sessionManager.resetTimers();
        }
    }, [
        showWarning,
        authService
    ]);
    // Setup session tracking
    useEffect(()=>{
        if (!isAuthenticated) {
            sessionManager.setAuthenticated(false);
            sessionTimeoutInstance.deactivate();
            return;
        }
        // Only initialize once globally using singleton
        if (sessionTimeoutInstance.activate()) {
            // Start tracking
            sessionManager.startTracking();
        }
        // Set callbacks for warning and timeout (these can update)
        sessionManager.setCallbacks(showWarningModal, handleSessionTimeout);
        // Add activity listeners for throttled extension
        const activityHandler = ()=>{
            handleUserActivity();
        };
        SESSION_TIMEOUT_CONFIG.ACTIVITY_EVENTS.forEach((event)=>{
            window.addEventListener(event, activityHandler);
        });
        // Cleanup
        return ()=>{
            SESSION_TIMEOUT_CONFIG.ACTIVITY_EVENTS.forEach((event)=>{
                window.removeEventListener(event, activityHandler);
            });
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
            }
        // Don't deactivate on cleanup - only on logout
        // This prevents re-initialization when components re-render
        };
    }, [
        isAuthenticated,
        showWarningModal,
        handleSessionTimeout,
        handleUserActivity
    ]);
    return {
        showWarning,
        timeRemaining,
        continueSession,
        logout: handleSessionTimeout
    };
};
